import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Form, Input, Select, message, Spin, DatePicker, Radio, Checkbox } from 'antd';
import dayjs from 'dayjs';
import { RiCheckLine } from 'react-icons/ri';
import { fetchEventDetail, registerEvent, recordEventScan, fetchMyEvents } from '../../services/eventService';
import AuthModal from '../../components/auth/AuthModal';
import { BASE_URL } from '../../constants/Url';
import defaultEventBanner from '../../assets/images/banner.png';

const { Option } = Select;
const { TextArea } = Input;

const getDeviceId = () => {
  try {
    let deviceId = localStorage.getItem('ESI_DEVICE_ID');
    if (!deviceId) {
      deviceId = `DEV_WEB_${Math.random().toString(36).substring(2, 10).toUpperCase()}_${Date.now()}`;
      localStorage.setItem('ESI_DEVICE_ID', deviceId);
    }
    return deviceId;
  } catch {
    return 'DEV_WEB_BROWSER';
  }
};

const getLocalizedFieldTitle = (q, t) => {
  if (!q) return '';
  const title = (q.title || '').trim();
  const type = q.questionType;

  if (title === '姓名' || title.toLowerCase() === 'name') {
    return t('text_shareform_title_name');
  }
  if (
    title === '手机号' ||
    title === '手机号码' ||
    title === '手机' ||
    title === '电话' ||
    title === '電話' ||
    title === '電話號碼' ||
    title === '手機' ||
    title === '手機號' ||
    title.toLowerCase().includes('phone') ||
    type === 'phone'
  ) {
    return t('text_shareform_title_phone');
  }
  if (
    title.includes('发廊') ||
    title.includes('髮廊') ||
    title.toLowerCase().includes('salon')
  ) {
    return t('text_shareform_title_salon');
  }
  if (
    title.includes('邀请码') ||
    title.includes('邀請碼') ||
    title.toLowerCase().includes('invitation') ||
    q.code === 'INVITATION-CODE'
  ) {
    return t('text_shareform_title_code');
  }
  if (
    title === '电邮' ||
    title === '電子郵箱' ||
    title === '电子邮箱' ||
    title === '邮箱' ||
    title === '郵箱' ||
    title.toLowerCase().includes('email') ||
    type === 'email'
  ) {
    return t('text_shareform_title_email');
  }
  return q.title || '';
};

const getLocalizedFieldPlaceholder = (q, t) => {
  if (!q) return '';
  const title = (q.title || '').trim();
  const placeholder = (q.placeholder || '').trim();
  const type = q.questionType;

  if (
    title === '姓名' ||
    title.toLowerCase() === 'name' ||
    placeholder.includes('姓名') ||
    placeholder.toLowerCase().includes('your name')
  ) {
    return t('text_shareform_name_placeholder');
  }
  if (
    title === '手机号' ||
    title === '手机号码' ||
    title === '手机' ||
    title === '电话' ||
    title === '電話' ||
    title === '電話號碼' ||
    title === '手機' ||
    title === '手機號' ||
    title.toLowerCase().includes('phone') ||
    placeholder.includes('手机') ||
    placeholder.includes('手機') ||
    placeholder.includes('电话') ||
    placeholder.includes('電話') ||
    type === 'phone'
  ) {
    return t('text_shareform_phone_placeholder');
  }
  if (
    title.includes('发廊') ||
    title.includes('髮廊') ||
    title.toLowerCase().includes('salon') ||
    placeholder.includes('发廊') ||
    placeholder.includes('髮廊') ||
    placeholder.includes('公司名称') ||
    placeholder.includes('公司名稱')
  ) {
    return t('text_shareform_salon_placeholder');
  }
  if (
    title.includes('邀请码') ||
    title.includes('邀請碼') ||
    title.toLowerCase().includes('invitation') ||
    placeholder.includes('邀请码') ||
    placeholder.includes('邀請碼') ||
    q.code === 'INVITATION-CODE'
  ) {
    return t('text_shareform_code_placeholder');
  }
  if (
    title === '电邮' ||
    title === '電子郵箱' ||
    title === '电子邮箱' ||
    title === '邮箱' ||
    title === '郵箱' ||
    title.toLowerCase().includes('email') ||
    placeholder.includes('邮箱') ||
    placeholder.includes('郵箱') ||
    placeholder.includes('电邮') ||
    placeholder.includes('電郵') ||
    type === 'email'
  ) {
    return t('text_shareform_email_placeholder');
  }
  return q.placeholder || t('text_fill_required');
};

const resolveEventErrorMessage = (rawMsg, t) => {
  if (!rawMsg) return t('text_submit_registration') + ' failed';
  if (typeof rawMsg === 'string' && (rawMsg.includes('邀请码无效') || rawMsg.includes('不存在') || rawMsg.toLowerCase().includes('invalid invite'))) {
    return t('text_error_invalid_invite_code');
  }
  return t(rawMsg) || rawMsg;
};

const getExpectedPhoneLength = (countryCode) => {
  const clean = String(countryCode || '').replace(/\D/g, '');
  return clean === '852' ? 8 : 11;
};

export default function ShareFormPage() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') || searchParams.get('eventId') || '';
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [event, setEvent] = useState(null);
  const [userRegistration, setUserRegistration] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedValuesPendingAuth, setSubmittedValuesPendingAuth] = useState(null);

  const authModalRef = useRef(null);
  const auth = useSelector((state) => state.authReducer);
  const user = auth?.user;
  const token = auth?.token;

  const currentLang = i18n.language === 'en' ? 'en' : (i18n.language === 'zh-HK' ? 'zh-HK' : 'cn');

  // Load event details and registration status
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!token || !id) {
        if (isMounted) setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const deviceId = getDeviceId();

        // 1. Initial scan record with formLink
        recordEventScan({
          deviceId,
          type: 'scan',
          formLink: id,
        });

        // 2. Fetch event detail
        const res = await fetchEventDetail(id);
        if (!isMounted) return;

        if (res && res.status !== 400 && (res.status === 200 || res.data)) {
          const eventData = res.data || res;
          setEvent(eventData);

          // 3. Confirm scan record with resolved eventId
          const resolvedEventId = eventData._id || eventData.eventId;
          if (resolvedEventId) {
            recordEventScan({
              deviceId,
              type: 'scan',
              eventId: resolvedEventId,
              formLink: id,
            });
          }

          // If current user is logged in, check existing registration status
          if (token) {
            try {
              const myEventsRes = await fetchMyEvents({
                keyword: eventData.eventId || id,
                pageSize: 10,
              });
              if (myEventsRes && myEventsRes.data && myEventsRes.data.length > 0) {
                const found = myEventsRes.data.find(
                  (r) =>
                    r.eventId === eventData.eventId ||
                    r.eventsId?._id === eventData._id ||
                    r.eventsId === eventData._id
                );
                if (found) {
                  setUserRegistration(found);
                }
              }
            } catch (err) {
              console.warn('Could not check user registration status:', err);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch event:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id, token]);

  const eventDetails = (typeof event?.eventsId === 'object' && event?.eventsId !== null) ? event.eventsId : (event || {});
  const localizeObj = event?.localize?.[currentLang] || event?.localize?.cn || eventDetails?.localize?.[currentLang] || eventDetails?.localize?.cn || {};
  const eventTitle = localizeObj?.title || event?.title || eventDetails?.title || '';
  const layout = localizeObj?.layout || [];

  // Auto-prefill form when user info or layout is available
  useEffect(() => {
    if (layout.length > 0) {
      const initialValues = {};
      layout.forEach((q) => {
        const title = (q.title || '').trim().toLowerCase();

        if (q.questionType === 'phone') {
          initialValues[`${q._id}_country`] = q.defaultCountryCode || '+86';
          if (user?.phone) {
            initialValues[q._id] = typeof user.phone === 'object' ? user.phone.number : user.phone;
          }
        } else if (q.questionType === 'email') {
          if (user?.email) {
            initialValues[q._id] = user.email;
          }
        } else if (q.questionType === 'single-choice' || q.questionType === 'select') {
          const preselected = (q.answers || []).find((a) => a && (a.isanswer === true || a.isanswer === 'true'));
          if (preselected) {
            initialValues[q._id] = preselected._id || preselected.answer;
          }
        } else if (q.questionType === 'multiple-choice') {
          const preselected = (q.answers || [])
            .filter((a) => a && (a.isanswer === true || a.isanswer === 'true'))
            .map((a) => a._id || a.answer);
          if (preselected.length > 0) {
            initialValues[q._id] = preselected;
          }
        } else if (title.includes('姓名') || title.includes('name')) {
          if (user?.name || user?.nickname || user?.username) {
            initialValues[q._id] = user.name || user.nickname || user.username;
          }
        } else if (title.includes('发廊') || title.includes('公司') || title.includes('salon') || title.includes('company')) {
          if (user?.salonName || user?.companyName || user?.salon) {
            initialValues[q._id] = user.salonName || user.companyName || user.salon;
          }
        }
      });
      form.setFieldsValue(initialValues);
    }
  }, [user, layout, form]);

  // Execute submission
  const executeSubmission = async (values) => {
    setSubmitting(true);
    try {
      const submittedLayout = layout.map((q) => {
        const rawVal = values[q._id];

        // 1. Phone question type
        if (q.questionType === 'phone') {
          const selectedCountry = values[`${q._id}_country`] || q.defaultCountryCode || '+86';
          const updatedCountryCodes = (q.countryCodes || []).map((c) => ({
            ...c,
            isanswer: c.value === selectedCountry || c.label === selectedCountry,
          }));

          const phoneStr = rawVal ? String(rawVal).trim() : '';
          return {
            ...q,
            answers: phoneStr ? [phoneStr] : [],
            countryCodes: updatedCountryCodes,
          };
        }

        // 2. Single-choice radio question type
        if (q.questionType === 'single-choice') {
          const updatedAnswers = (q.answers || []).map((opt) => ({
            ...opt,
            isanswer: opt._id === rawVal || opt.answer === rawVal,
          }));
          return {
            ...q,
            answers: updatedAnswers,
          };
        }

        // 3. Multiple-choice checkbox question type
        if (q.questionType === 'multiple-choice') {
          const selectedArray = Array.isArray(rawVal) ? rawVal : (rawVal ? [rawVal] : []);
          const updatedAnswers = (q.answers || []).map((opt) => ({
            ...opt,
            isanswer: selectedArray.includes(opt._id) || selectedArray.includes(opt.answer),
          }));
          return {
            ...q,
            answers: updatedAnswers,
          };
        }

        // 4. Dropdown Select question type
        if (q.questionType === 'select') {
          const updatedAnswers = (q.answers || []).map((opt) => ({
            ...opt,
            isanswer: opt._id === rawVal || opt.answer === rawVal,
          }));
          return {
            ...q,
            answers: updatedAnswers,
          };
        }

        // 5. Date question type
        if (q.questionType === 'date') {
          let dateStr = '';
          if (rawVal) {
            if (rawVal.format) {
              dateStr = rawVal.format('YYYY-MM-DD');
            } else if (dayjs(rawVal).isValid()) {
              dateStr = dayjs(rawVal).format('YYYY-MM-DD');
            } else {
              dateStr = String(rawVal);
            }
          }
          return {
            ...q,
            answers: dateStr ? [dateStr] : [],
          };
        }

        // 6. Number question type
        if (q.questionType === 'number') {
          const numStr = (rawVal !== undefined && rawVal !== null && rawVal !== '') ? String(rawVal).trim() : '';
          return {
            ...q,
            answers: numStr ? [numStr] : [],
          };
        }

        // 7. Standard Text / Email / Textarea / other types
        let answerVal = '';
        if (Array.isArray(rawVal)) {
          answerVal = rawVal.join(', ');
        } else if (rawVal !== undefined && rawVal !== null) {
          answerVal = String(rawVal).trim();
        }

        return {
          ...q,
          answers: answerVal ? [answerVal] : [],
        };
      });

      const langKey = eventDetails?.localize?.[currentLang] ? currentLang : 'cn';
      const isValidObjectId = (val) => typeof val === 'string' && /^[0-9a-fA-F]{24}$/.test(val);

      // Backend model "events" requires a 24-hex-char MongoDB ObjectId.
      // eventDetails._id is the ObjectId (e.g. "6ad713acab3c49c4d374bccb").
      // eventDetails.eventId is the display code (e.g. "EV16800001"), which causes "Cast to ObjectId failed".
      const eventObjectId =
        (isValidObjectId(eventDetails?._id) && eventDetails._id) ||
        (isValidObjectId(event?._id) && event._id) ||
        (isValidObjectId(eventDetails?.eventsId?._id) && eventDetails.eventsId._id) ||
        (isValidObjectId(eventDetails?.eventsId) && eventDetails.eventsId) ||
        (isValidObjectId(id) && id);

      if (!eventObjectId) {
        message.error(t('text_shareform_invalid_link') || 'Event is invalid!');
        return;
      }

      const payload = {
        eventId: eventObjectId,
        localize: {
          [langKey]: {
            title: eventTitle || '',
            layout: submittedLayout,
          },
        },
      };

      const res = await registerEvent(payload);
      if (res && (res.status === 200 || res.status === 201 || res.data)) {
        setUserRegistration(res.data || { approvalStatus: 'pending' });
        setShowSuccessModal(true);
        setSubmittedValuesPendingAuth(null);
      } else {
        message.error(resolveEventErrorMessage(t(res?.message), t));
      }
    } catch (err) {
      console.error('Submission failed:', err);
      const rawMsg = err?.response?.data?.message || err?.message;
      message.error(resolveEventErrorMessage(t(rawMsg), t));
    } finally {
      setSubmitting(false);
    }
  };

  // Form submit handler
  const handleFormFinish = async (values) => {
    // If not logged in, trigger auth modal and queue values for immediate post-auth submission
    if (!token) {
      setSubmittedValuesPendingAuth(values);
      authModalRef.current?.open();
      return;
    }

    await executeSubmission(values);
  };

  const handleFormFinishFailed = (errorInfo) => {
    if (errorInfo?.errorFields?.length > 0) {
      const firstError = errorInfo.errorFields[0];
      const errorMsg = firstError?.errors?.[0] || t('text_fill_required');
      message.error(errorMsg);
      if (firstError?.name) {
        form.scrollToField(firstError.name, { behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Callback when AuthModal completes login/register
  const handleAuthSuccess = async () => {
    if (submittedValuesPendingAuth) {
      await executeSubmission(submittedValuesPendingAuth);
    }
  };

  // Banner image resolution
  const images = (eventDetails.images && eventDetails.images.length > 0) ? eventDetails.images : (event?.images || []);
  const bannerUrl = images.length > 0
    ? (images[0]?.url || images[0])
    : (eventDetails.coverImage || event?.coverImage || defaultEventBanner);

  if (!token) {
    const returnUrl = location.pathname + location.search;
    try {
      sessionStorage.setItem('ESI_REDIRECT_URL', returnUrl);
    } catch {}
    return (
      <Navigate
        to="/"
        replace
        state={{
          openLogin: true,
          returnUrl,
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center py-20 px-4">
        <Spin size="large" />
        <p className="mt-4 text-sm text-gray-500 font-medium">{t('Loading event details...')}</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 max-w-md w-full">
          <h2 className="text-xl font-bold text-gray-800 mb-2">{t('text_shareform_invalid_link')}</h2>
          <p className="text-sm text-gray-500 mb-6">{t('text_no_activities')}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full py-2.5 px-4 bg-[#e50a47] text-white rounded-lg font-medium hover:bg-[#cf1342] transition-colors cursor-pointer"
          >
            {t('text_back_to_activities')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-[#e50a47] selection:text-white pb-20">
      {/* ── Top Hero Banner Image ── */}
      {bannerUrl && (
        <div className="w-full bg-black flex justify-center items-center overflow-hidden">
          <img
            src={bannerUrl}
            alt={eventTitle || 'Event Banner'}
            className="w-full h-auto max-h-[520px] object-cover object-center block"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = BASE_URL + '/logo.svg';
            }}
          />
        </div>
      )}

      {/* ── Registered Status Alert (if user has already registered) ── */}
      {userRegistration && !showSuccessModal && (
        <div className="max-w-[33rem] mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-16"> 
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6 sm:p-8 flex flex-col items-center text-center shadow-sm">
            <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-sm shadow-emerald-500/20 mb-3">
              <RiCheckLine size={28} />
            </div>
            <h4 className="text-base sm:text-lg font-bold text-emerald-950 m-0">
              {t('text_shareform_already_registered')}
            </h4>
            <p className="text-xs sm:text-sm text-emerald-700 m-0 mt-1">
              {t('text_status_' + (userRegistration.approvalStatus || 'pending')) || '已提交审核'}
            </p>
            <button
              onClick={() => navigate('/my-activities')}
              className="w-full max-w-[220px] sm:max-w-[240px] py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-lg text-sm font-semibold transition-all shadow-sm shadow-emerald-600/20 cursor-pointer mt-5"
            >
              {t('text_shareform_view_activities')}
            </button>
          </div>
        </div>
      )}

      {/* ── Main Form Section (only shown when not registered) ── */}
      {!userRegistration && (
        <div className="max-w-[760px] mx-auto px-4 sm:px-6 pt-8 sm:pt-12">
        <Form
          form={form}
          layout="horizontal"
          onFinish={handleFormFinish}
          onFinishFailed={handleFormFinishFailed}
          scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
          requiredMark={false}
          className="space-y-4 sm:space-y-5"
        >
          {layout.map((q) => {
            const fieldTitle = getLocalizedFieldTitle(q, t);
            const fieldPlaceholder = getLocalizedFieldPlaceholder(q, t);
            const rules = [];
            if (q.questionType === 'phone') {
              rules.push({
                validator(_, value) {
                  const currentCountry = form.getFieldValue(`${q._id}_country`) || q.defaultCountryCode || '+86';
                  const expectedLength = getExpectedPhoneLength(currentCountry);
                  const cleanDigits = String(value || '').replace(/\D/g, '');
                  const cleanCountry = String(currentCountry).replace(/\D/g, '');
                  const errMsg = cleanCountry === '852'
                    ? (t('text_invalid_phone_digits_hk') || 'Hong Kong phone number must be 8 digits')
                    : (t('text_invalid_phone_digits_cn') || 'China phone number must be 11 digits');

                  if (!cleanDigits) {
                    if (q.isRequired) {
                      return Promise.reject(new Error(errMsg));
                    }
                    return Promise.resolve();
                  }

                  if (cleanDigits.length !== expectedLength) {
                    return Promise.reject(new Error(errMsg));
                  }
                  return Promise.resolve();
                },
              });
            } else {
              if (q.isRequired) {
                rules.push({ required: true, message: t('text_fill_required') });
              }
              if (q.questionType === 'email') {
                rules.push({ type: 'email', message: t('Please enter a valid email address') || 'Invalid email format' });
              }
            }

            // Custom field label with clean asterisk
            const labelNode = (
              <div className="w-full sm:w-[215px] shrink-0 text-left text-gray-800 text-sm sm:text-base font-normal pt-1 sm:pt-2 sm:pr-4 select-none whitespace-nowrap">
                <span>{fieldTitle}</span>
                {q.isRequired && <span className="text-[#e50a47] ml-1">*</span>}
              </div>
            );

            // Phone type with dropdown prefix
            if (q.questionType === 'phone') {
              const countryCodes = q.countryCodes && q.countryCodes.length > 0
                ? q.countryCodes
                : [
                    { label: '+86', value: '+86', _id: 'cn' },
                    { label: '+852', value: '+852', _id: 'hk' },
                  ];

              return (
                <div key={q._id} className="flex flex-col sm:flex-row sm:items-start">
                  {labelNode}
                  <div className="flex-1 w-full mt-1 sm:mt-0">
                    <div className="flex gap-2.5 items-start">
                      <Form.Item
                        name={`${q._id}_country`}
                        noStyle
                        initialValue={q.defaultCountryCode || '+86'}
                      >
                        <Select
                          style={{ width: 96 }}
                          size="large"
                          onChange={(val) => {
                            const currentVal = form.getFieldValue(q._id);
                            if (currentVal) {
                              const cleanDigits = String(currentVal).replace(/\D/g, '');
                              const maxLen = getExpectedPhoneLength(val);
                              form.setFieldsValue({ [q._id]: cleanDigits.slice(0, maxLen) });
                            }
                          }}
                          className="!h-10 rounded-md font-medium [&_.ant-select-selector]:!h-10 [&_.ant-select-selector]:!rounded-md [&_.ant-select-selector]:!border-gray-200 hover:[&_.ant-select-selector]:!border-gray-300 [&_.ant-select-selection-item]:!leading-[38px] [&_.ant-select-arrow]:!top-1/2 [&_.ant-select-arrow]:!-translate-y-1/2 [&_.ant-select-arrow]:!m-0 [&.ant-select-focused_.ant-select-selector]:!border-[#1677ff] [&.ant-select-open_.ant-select-selector]:!border-[#1677ff]"
                        >
                          {countryCodes.map((code) => (
                            <Option key={code._id || code.value} value={code.value}>
                              {code.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        name={q._id}
                        rules={rules}
                        validateTrigger={[]}
                        noStyle
                        normalize={(value) => {
                          if (!value) return '';
                          const digits = String(value).replace(/\D/g, '');
                          const currentCountry = form.getFieldValue(`${q._id}_country`) || q.defaultCountryCode || '+86';
                          const maxLen = getExpectedPhoneLength(currentCountry);
                          return digits.slice(0, maxLen);
                        }}
                      >
                        <Input
                          placeholder={fieldPlaceholder}
                          size="large"
                          className="!h-10 rounded-md flex-1 text-sm sm:text-base border-gray-200 hover:border-gray-300 focus:border-[#1677ff]"
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              );
            }

            // Single-choice Radio type
            if (q.questionType === 'single-choice') {
              return (
                <div key={q._id} className="flex flex-col sm:flex-row sm:items-start">
                  {labelNode}
                  <div className="flex-1 w-full mt-1 sm:mt-0">
                    <Form.Item name={q._id} rules={rules} noStyle>
                      <Radio.Group className="flex flex-col gap-2.5 pt-1.5 w-full">
                        {(q.answers || []).map((opt) => (
                          <Radio
                            key={opt._id || opt.answer}
                            value={opt._id || opt.answer}
                            className="text-sm sm:text-base text-gray-800"
                          >
                            {opt.answer}
                          </Radio>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  </div>
                </div>
              );
            }

            // Multiple-choice Checkbox type
            if (q.questionType === 'multiple-choice') {
              return (
                <div key={q._id} className="flex flex-col sm:flex-row sm:items-start">
                  {labelNode}
                  <div className="flex-1 w-full mt-1 sm:mt-0">
                    <Form.Item name={q._id} rules={rules} noStyle>
                      <Checkbox.Group className="flex flex-col gap-2.5 pt-1.5 w-full">
                        {(q.answers || []).map((opt) => (
                          <Checkbox
                            key={opt._id || opt.answer}
                            value={opt._id || opt.answer}
                            className="text-sm sm:text-base text-gray-800 !ml-0"
                          >
                            {opt.answer}
                          </Checkbox>
                        ))}
                      </Checkbox.Group>
                    </Form.Item>
                  </div>
                </div>
              );
            }

            // Select Dropdown type
            if (q.questionType === 'select') {
              return (
                <div key={q._id} className="flex flex-col sm:flex-row sm:items-start">
                  {labelNode}
                  <div className="flex-1 w-full mt-1 sm:mt-0">
                    <Form.Item name={q._id} rules={rules} noStyle>
                      <Select
                        size="large"
                        placeholder={fieldPlaceholder}
                        className="w-full !h-10 rounded-md font-medium [&_.ant-select-selector]:!h-10 [&_.ant-select-selector]:!rounded-md [&_.ant-select-selector]:!border-gray-200 hover:[&_.ant-select-selector]:!border-gray-300 [&_.ant-select-selection-item]:!leading-[38px] [&_.ant-select-arrow]:!top-1/2 [&_.ant-select-arrow]:!-translate-y-1/2 [&_.ant-select-arrow]:!m-0 [&.ant-select-focused_.ant-select-selector]:!border-[#1677ff] [&.ant-select-open_.ant-select-selector]:!border-[#1677ff]"
                      >
                        {(q.answers || []).map((opt) => (
                          <Option key={opt._id || opt.answer} value={opt._id || opt.answer}>
                            {opt.answer}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                </div>
              );
            }

            // Number type
            if (q.questionType === 'number') {
              return (
                <div key={q._id} className="flex flex-col sm:flex-row sm:items-start">
                  {labelNode}
                  <div className="flex-1 w-full mt-1 sm:mt-0">
                    <Form.Item name={q._id} rules={rules} noStyle>
                      <Input
                        type="number"
                        placeholder={fieldPlaceholder}
                        size="large"
                        className="w-full !h-10 rounded-md text-sm sm:text-base border-gray-200 hover:border-gray-300 focus:border-[#1677ff]"
                      />
                    </Form.Item>
                  </div>
                </div>
              );
            }

            // Date Picker type
            if (q.questionType === 'date') {
              return (
                <div key={q._id} className="flex flex-col sm:flex-row sm:items-start">
                  {labelNode}
                  <div className="flex-1 w-full mt-1 sm:mt-0">
                    <Form.Item name={q._id} rules={rules} noStyle>
                      <DatePicker
                        size="large"
                        className="w-full !h-10 rounded-md border-gray-200 hover:border-gray-300 focus:border-[#1677ff]"
                        placeholder={fieldPlaceholder}
                      />
                    </Form.Item>
                  </div>
                </div>
              );
            }

            // Textarea type
            if (q.questionType === 'textarea') {
              return (
                <div key={q._id} className="flex flex-col sm:flex-row sm:items-start">
                  {labelNode}
                  <div className="flex-1 w-full mt-1 sm:mt-0">
                    <Form.Item name={q._id} rules={rules} noStyle>
                      <TextArea
                        rows={3}
                        placeholder={fieldPlaceholder}
                        className="w-full rounded-md border-gray-200 hover:border-gray-300 focus:border-[#1677ff] p-2.5 text-sm sm:text-base"
                      />
                    </Form.Item>
                  </div>
                </div>
              );
            }

            // Email Input
            if (q.questionType === 'email') {
              return (
                <div key={q._id} className="flex flex-col sm:flex-row sm:items-start">
                  {labelNode}
                  <div className="flex-1 w-full mt-1 sm:mt-0">
                    <Form.Item name={q._id} rules={rules} noStyle>
                      <Input
                        type="email"
                        placeholder={fieldPlaceholder}
                        size="large"
                        className="w-full !h-10 rounded-md text-sm sm:text-base border-gray-200 hover:border-gray-300 focus:border-[#1677ff]"
                      />
                    </Form.Item>
                  </div>
                </div>
              );
            }

            // Standard Text Input (default)
            return (
              <div key={q._id} className="flex flex-col sm:flex-row sm:items-start">
                {labelNode}
                <div className="flex-1 w-full mt-1 sm:mt-0">
                  <Form.Item name={q._id} rules={rules} noStyle>
                    <Input
                      placeholder={fieldPlaceholder}
                      size="large"
                      className="w-full !h-10 rounded-md text-sm sm:text-base border-gray-200 hover:border-gray-300 focus:border-[#1677ff]"
                    />
                  </Form.Item>
                </div>
              </div>
            );
          })}

          {/* ── Submit Button with Decorative Line (matching Screenshot 1) ── */}
          <div className="pt-6 sm:pt-8 flex flex-col sm:flex-row sm:items-center">

            <div className="flex-1 flex items-center justify-center w-full">
              <button
                type="submit"
                disabled={submitting}
                className="bg-[#e5184b] hover:bg-[#cf1342] active:bg-[#b00f37] text-white font-medium text-sm sm:text-base px-8 py-3 rounded-lg shadow-sm transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed shrink-0 flex items-center justify-center"
              >
                {submitting && <Spin size="small" />}
                <span>{t('text_shareform_submit')}</span>
              </button>

            </div>
          </div>
        </Form>
      </div>
      )}

      {/* ── Celebration Success Modal (matching Screenshot 2) ── */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-[2px] animate-fade-in">
          <div className="relative w-full max-w-[30rem] bg-white rounded-2xl sm:rounded-3xl pt-14 pb-8 px-6 sm:px-12 shadow-2xl text-center transform transition-all overflow-visible border border-gray-100/50">
            {/* Top spring-lime yellow-green gradient glow matching screenshot */}
            <div className="absolute top-0 left-0 right-0 h-44 rounded-t-2xl sm:rounded-t-3xl bg-gradient-to-b from-[#cbf775] via-[#e8fdb5]/65 via-50% to-transparent pointer-events-none" />

            {/* Success Icon Cluster with Concentric Halo and Confetti Dots (centered on top edge) */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none">
              {/* Outer translucent green concentric halo */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[98px] h-[98px] rounded-full bg-[#2bd968]/25" />

              {/* Dot 1: Top-Left golden yellow dot (~10 o'clock) */}
              <div className="absolute -left-12 -top-2 w-3 h-3 rounded-full bg-[#f1b225]" />

              {/* Dot 2: Top-Right vibrant sky-blue dot (~1 o'clock) */}
              <div className="absolute left-11 -top-11 w-1.5 h-1.5 rounded-full bg-[#38bdf8]" />

              {/* Dot 3: Green dot resting on the top-right halo edge (~2 o'clock) */}
              <div className="absolute left-[3.6rem] -top-[0.25rem] w-[0.6rem] h-[0.6rem] rounded-full bg-[#22c55e]" />

              {/* Dot 4: Large golden yellow dot on right side below edge line (~4 o'clock) */}
              <div className="absolute left-[5rem] top-[2.5rem] w-4 h-4 rounded-full bg-[#f5be38]" />

              {/* Dot 5: Small pale yellow dot at lower right (~5:30) */}
              <div className="absolute left-6 top-13 w-2 h-2 rounded-full bg-[#fcd34d]" />

              {/* Main Green Check Circle (64x64px) with crisp white checkmark */}
              <div className="relative w-16 h-16 rounded-full bg-[#2ed058] flex items-center justify-center shadow-md shadow-emerald-600/30">
                <svg
                  className="w-8 h-8 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>

            {/* Modal Title */}
            <h3 className="relative text-xl sm:text-[22px] font-bold text-gray-900 mb-3 tracking-normal">
              {t('text_shareform_success_title')}
            </h3>

            {/* Notification Text */}
            <p className="relative text-xs sm:text-[13.5px] text-gray-700 leading-relaxed max-w-[440px] mx-auto mb-6 px-1 text-center font-normal">
              {t('text_shareform_success_desc')}
            </p>

            {/* Action Button: "我知道了" */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="relative w-full max-w-[180px] sm:max-w-[190px] mx-auto py-2.5 px-6 rounded-lg text-white font-medium text-sm sm:text-base bg-[#e5184b] hover:bg-[#cf1342] active:bg-[#b00f37] transition-all shadow-md shadow-pink-500/20 cursor-pointer block"
            >
              {t('text_shareform_got_it')}
            </button>
          </div>
        </div>
      )}

      {/* ── Phone Login Modal for Guests ── */}
      <AuthModal ref={authModalRef} onAuthSuccess={handleAuthSuccess} />
    </div>
  );
}
